package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * UI 元素节点（递归）。
 *
 * @property kind 元素 kind（box/row/text/button/...）
 * @property id 元素显式 id（即 DSL 中的字符串字面量）
 * @property props 属性赋值列表
 * @property children 子元素
 * @property isChildrenSlot 当为 true 时表示组件 root 内的 `children` 占位标记，此时 kind/id/props/children 字段忽略
 * @property slotName 具名插槽名：仅当 [isChildrenSlot] 为 true 时有意义；null 表示匿名洞（`children`），
 *                    非 null 表示具名洞（`children "header"`）。
 * @property namedSlotContent 组件调用方按名分组的具名插槽内容：键为插槽名，值为该名下的子元素列表。
 *                    仅组件调用元素非空；匿名内容仍走 [children]。展开期按 [slotName] 分发到对应命名洞。
 * @property canvasData 若本元素为 canvas（kind == "canvas"），承载解析后的 canvas AST；
 *                      其它 kind 必须为 null。由 ElementEmitter 编译为 [UiNode.canvasData]。
 */
data class ElementAst(
    val kind: String,
    val id: String,
    val props: List<PropAssign>,
    val children: List<ElementAst>,
    val isChildrenSlot: Boolean = false,
    val slotName: String? = null,
    val namedSlotContent: Map<String, List<ElementAst>> = emptyMap(),
    val canvasData: CanvasAst? = null,
    /**
     * M6-b：容器形参列表（`column "x" { item -> ... }` / `column "y" { k, v -> ... }`）。
     *
     * 仅在容器块声明形参列表时非空；普通容器为空 List。
     */
    val sourceParams: List<SourceParam> = emptyList(),
    /**
     * M6-b：容器 `source = expr` 属性的表达式形态。
     *
     * 与 props["source"] 同步存在（M6-c 才迁出 props），在此暴露字段方便后续语义/IR 阶段访问。
     */
    val sourceExpr: ExprAst? = null,
    /**
     * tooltip 内容引用；为空表示未声明 tooltip。
     */
    val tooltipContent: TooltipContentAst? = null,
    /**
     * toast 富内容引用；为空表示未声明 content 块或组件内容。
     */
    val toastContent: TooltipContentAst? = null,
    /**
     * 右键菜单内容引用；为空表示未声明 rightClickMenu。
     */
    val rightClickMenuContent: TooltipContentAst.Component? = null,
    /**
     * 上下文菜单内容引用；为空表示未声明 contextMenu。
     */
    val contextMenuContent: TooltipContentAst.Component? = null,
    /**
     * 顶层菜单块声明；当前 Stage E 仅解析并进入 overlay 描述通道。
     */
    val menuBlocks: List<MenuContentAst> = emptyList(),
    /**
     * M6-d：标记本元素是从字符串字面量 kind（`"name" "id" {...}`）解析而来。
     *
     * 对此类元素，[top.wcpe.allincore.dsl.compiler.pipeline.ComponentExpanderPass]
     * 一律按组件调用处理（无需 kind 首字母大写）。
     */
    val isStringKindCall: Boolean = false,
    /**
     * RFC-RECOMPOSITION：条件分支内容引用。
     *
     * 仅当 [kind] == [ConditionalContentAst.KIND_CONDITIONAL] 时非 null；其他元素必须为 null。
     * 编译期把元素树位置的 `if/when` 语法包装成一个 kind="$cond" 的 ElementAst，
     * 实际分支元素由 ConditionalContentAst 描述。运行时解释器据此分派到 buildConditionalContainer。
     */
    val conditionalContent: ConditionalContentAst? = null,
    /**
     * 元素树位置 for 循环重组内容引用。
     *
     * 仅当 [kind] == [ForContentAst.KIND_FOR] 时非 null；其他元素必须为 null。
     * 编译期把元素树位置的 `for` 语法包装成一个 kind="$for" 的 ElementAst，
     * 实际循环体由 ForContentAst 描述。运行时解释器据此分派到 buildForContainer。
     */
    val forContent: ForContentAst? = null,
    /**
     * 编译期 style-class 样式层：元素声明的样式类名列表（`class = "danger primary"` 按空白切分）。
     *
     * 纯编译期产物：由 [top.wcpe.allincore.dsl.compiler.pipeline.StyleClassApplyPass] 据此把
     * screen 顶层 `styles { ... }` 块中命中的规则 props 合并进本元素 props，合并后即被清空，
     * 绝不进入 IR / prop 校验。
     */
    val styleClasses: List<String> = emptyList(),
    /**
     * FR-29：元素有序视觉 modifier 链。由 `modifiers {}` / `effects {}` 与迁移后的样式属性生成，
     * codegen 后进入 UiNode.modifiers，不再占用普通 props。
     */
    val modifiers: List<ModifierAst> = emptyList(),
    val themeOverride: ThemeAst? = null,
    override val position: SourcePosition,
) : AstNode {

    /** 单个属性赋值。 */
    data class PropAssign(
        val name: String,
        val value: ExprAst,
        override val position: SourcePosition,
    ) : AstNode {
        override val range
            get() = position.range.merge(value.range)
    }

    /**
     * M6-b：容器块形参（List 形态单参；Map 形态双参；`_` 表示丢弃）。
     *
     * - [name] 形参名，`_` 为丢弃占位
     * - [type] 可选类型标注；未标注为 null，由 source 元素类型推断
     * - [inferredType] M6-c Stage 4：当 [type] 为 null 时，由
     *   [top.wcpe.allincore.dsl.compiler.semantic.SourceContainerChecker]
     *   依据 source 静态类型反向推断后填充；不参与 equals/hashCode
     */
    data class SourceParam(
        val name: String,
        val type: TypeAst?,
        val position: SourcePosition,
    ) {
        /** M6-c Stage 4：从 source 反向推断的形参类型；显式标注时保持 null。 */
        var inferredType: TypeAst? = null
    }
}
